import { Injectable } from '@angular/core';
import { IUser } from '@portal/entity-services/interfaces/src';
import { INavbarItem } from '@portal/shared/ui/layouts/src/lib/top-navbar/interfaces/navbar-item.interface';
import {
  getNavbarItemLink,
  getSubNavItems,
  NavbarItemLink,
} from '../../helpers/navigation-route-helpers';
import { ApplicationRoot } from '../../enums/application-root.enum';
import { PetroService } from './petro.service';
import { MenuRegion } from '../../../../../ui/layouts/src/lib/top-navbar/enum/menu-region.enum';

@Injectable({
  providedIn: 'root',
})
export class PetroNavbarService {
  languageRoute: string;
  currentApplication: ApplicationRoot;

  constructor(private petroService: PetroService) {}

  getPetroNavbarItems(params: {
    currentApplication: ApplicationRoot;
    languageRoute: string;
    user: IUser;
  }): INavbarItem | null {
    this.languageRoute = params.languageRoute;
    this.currentApplication = params.currentApplication;
    const overview = this.getOverviewNavbarItems();
    const viewsItems = this.getViewsNavbarItems();
    const posConfig = this.getPosConfigNavbarItems();
    const helpdeskItems = this.getHelpdeskNavbarItems();

    return getSubNavItems(
      {
        id: 'administration',
        title: $localize`:Main menu navbar group name:Commander Central`,
        attributes: {
          'data-e2e': 'title-commander-central',
          groupTitle: $localize`Commander Central`,
          description: $localize`Configure, manage and monitor every aspect of your convenience store through a single pane of glass with Commander Central.`,
        },
        region: MenuRegion.PetroConfiguration,
      },
      [overview, helpdeskItems, viewsItems, posConfig],
    );
  }

  getOverviewNavbarItems(): INavbarItem | null {
    const overviewNavbarItems = {
      title: $localize`:Main menu navbar subgroup name:Overview`,
      attributes: {
        'data-e2e': 'title-overview',
      },
      children: [],
    };
    if (this.petroService.isPetroUser()) {
      overviewNavbarItems.children.push({
        title: $localize`:Main menu navbar item name:Dashboard`,
        attributes: {
          'data-e2e': 'link-petro-dashboard',
        },
        ...this.getItemUrl('/petro-dashboard', [ApplicationRoot.Petro]),
      });
    }

    return overviewNavbarItems;
  }

  getViewsNavbarItems(): INavbarItem | null {
    const viewsNavbarItems = {
      title: $localize`:Main menu navbar subgroup name:Views`,
      attributes: {
        'data-e2e': 'title-views',
      },
      children: [],
    };
    if (this.petroService.showTableConfig()) {
      viewsNavbarItems.children.push({
        title: $localize`:Main menu navbar item name:Table Views`,
        attributes: {
          'data-e2e': 'link-views',
        },
        ...this.getItemUrl('/configuration/tables', [ApplicationRoot.Petro]),
      });
    }

    return viewsNavbarItems;
  }

  getPosConfigNavbarItems(): INavbarItem | null {
    const posConfig = {
      title: $localize`:Main menu navbar subgroup name:Configuration`,
      attributes: {
        'data-e2e': 'title-pos-config',
      },
      children: [],
    };

    if (this.petroService.showMajorOilSettings()) {
      posConfig.children.push({
        title: $localize`:Main menu navbar item name:Major Oil Settings`,
        attributes: {
          'data-e2e': 'link-major-oil-settings',
        },
        ...this.getItemUrl('/configuration/major-oil-settings', [ApplicationRoot.Petro]),
      });
    }

    if (this.petroService.showSiteConfig()) {
      posConfig.children.push({
        title: $localize`:Main menu navbar item name:Site Management`,
        attributes: {
          'data-e2e': 'link-program-site-management',
        },
        ...this.getItemUrl('/configuration/site-management', [ApplicationRoot.Petro]),
      });
    }

    if (this.petroService.showEnterpriseConfig()) {
      posConfig.children.push({
        title: $localize`:Main menu navbar item name:Enterprise Management`,
        attributes: {
          'data-e2e': 'link-program-enterprise-management',
        },
        ...this.getItemUrl('/configuration/enterprise-management', [ApplicationRoot.Petro]),
      });
    }

    if (this.petroService.showPolicyManagement()) {
      posConfig.children.push({
        title: $localize`:Main menu navbar item name:Policy Management`,
        attributes: {
          'data-e2e': 'link-program-policy-management',
        },
        ...this.getItemUrl('/configuration/policy-management', [ApplicationRoot.Petro]),
      });
    }
    return posConfig;
  }

  getHelpdeskNavbarItems(): INavbarItem | null {
    const helpdesk = {
      title: $localize`:Main menu navbar subgroup name:Support`,
      attributes: { 'data-e2e': 'title-support' },
      children: [],
    };

    if (this.petroService.showLogPullMenu()) {
      helpdesk.children.push({
        title: $localize`:Main menu navbar item name:Log Pull`,
        attributes: { 'data-e2e': 'link-log-pull' },
        ...this.getItemUrl('/support/log-pull', [ApplicationRoot.Petro]),
      });
    }

    if (this.petroService.showLogLevelMenu()) {
      helpdesk.children.push({
        title: $localize`:Main menu navbar item name:Log Level`,
        attributes: { 'data-e2e': 'link-log-level' },
        ...this.getItemUrl('/support/log-pull/log-level', [ApplicationRoot.Petro]),
      });
    }

    return helpdesk;
  }

  private getItemUrl(url: string, applications: ApplicationRoot[]): NavbarItemLink {
    return getNavbarItemLink(this.currentApplication, applications, this.languageRoute, url);
  }
}
